import React from 'react';
import { twMerge } from 'tailwind-merge';

import { type CommonProps, Container, calcSpacing, styleVars } from 'gooey';

export type PageSectionProps = React.PropsWithChildren<
  {
    /**
     * The id of the section, used for linking to the section.
     */
    id?: string;
    /**
     * The amount of space to add to the top of the section.
     */
    gutterTop?: number;
    /**
     * The amount of space to add to the bottom of the section.
     */
    gutterBottom?: number;
    /**
     * If true, the section will not have any padding on the top and bottom.
     * @default false
     */
    disableGutters?: boolean;
    /**
     * If true, the children of the section will be centered.
     * @default false
     */
    center?: boolean;
  } & CommonProps
>;

function PageSection({
  children,
  className,
  gutterTop,
  gutterBottom,
  center = false,
  disableGutters = false,
  style,
  ...props
}: PageSectionProps) {
  const gutterClassnames = React.useMemo(() => {
    if (disableGutters) {
      return '';
    }

    const c = [];

    if (gutterTop === undefined) {
      c.push(defaults.gutterTop);
    } else {
      c.push('pt-(--page-section-gutter-top)');
    }

    if (gutterBottom === undefined) {
      c.push(defaults.gutterBottom);
    } else {
      c.push('pb-(--page-section-gutter-bottom)');
    }

    return c;
  }, [gutterTop, gutterBottom, disableGutters]);

  return (
    <Container
      {...props}
      className={twMerge(
        'bg-background-default w-full relative',
        center && 'text-center mx-auto',
        gutterClassnames,
        className
      )}
      component='section'
      style={{
        ...styleVars(
          {
            '--page-section-gutter-top': gutterTop,
            '--page-section-gutter-bottom': gutterBottom,
          },
          (x) => calcSpacing(Number(x))
        ),
        ...style,
      }}
    >
      {children}
    </Container>
  );
}
PageSection.displayName = 'PageSection';

export { PageSection };

/*
|------------------
| Internals
|------------------
*/

const defaults = {
  gutterTop: 'pt-16 md:pt-20 lg:pt-32',
  gutterBottom: 'pb-16 md:pb-20 lg:pb-32',
};
